import { useMutation } from '@apollo/client'
import { toast } from 'sonner'

import { UPDATE_STOP_ARRIVAL, UPDATE_STOP_DEPARTURE, UPDATE_STOP_ETA } from '../../../../../../data/mutations/stops'
import type { IDateTime } from '../../../../../../types/post'
import type { IStop } from '../../../../../../types/shipment'
import { StopSourceEnum } from '../../../../../../types/stop'
import { to24HourTime } from '../../../../../../utils/datetime-utils'
import { useStops } from '../contexts/use-stops-context'

type TimeUpdateType = 'eta' | 'arrival' | 'departure'

const SUCCESS_MESSAGES = {
  arrival: 'Check-in time updated successfully',
  departure: 'Check-out time updated successfully',
  eta: 'ETA updated successfully'
}

const STOP_FIELDS = {
  arrival: 'arrivalDateTime',
  departure: 'departureDateTime',
  eta: 'eta'
} as const

const MUTATIONS = {
  arrival: UPDATE_STOP_ARRIVAL,
  departure: UPDATE_STOP_DEPARTURE,
  eta: UPDATE_STOP_ETA
}

/**
 * This hook is used to update the arrival, departure or ETA of a stop.
 */
export const useUpdateStopFields = (type: TimeUpdateType, stop: IStop) => {
  const { shipment, stops, updateStops } = useStops()
  // Use the appropriate mutation
  const [updateTime] = useMutation(MUTATIONS[type], {
    onCompleted: () => {
      toast.success(SUCCESS_MESSAGES[type])
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const handleFieldUpdate = (dateTime: IDateTime | null) => {
    // Update local state
    const updatedStop: IStop = {
      ...stop,
      [STOP_FIELDS[type]]: dateTime
    }
    updateStops(stops.map((s) => (s.id === stop.id ? updatedStop : s)))

    // Prepare mutation input
    const input = {
      date: dateTime?.date || '',
      shipmentID: shipment.id,
      source: StopSourceEnum.ChaineWebApp,
      stopID: stop.id,
      time: to24HourTime(dateTime?.time) || null
    }
    toast.info('Updating stop details...')
    updateTime({
      variables: input
    })
  }

  return { handleFieldUpdate }
}
